import { type ReactElement } from "react";
import {
  CheckCheck,
  Mail,
  MonitorSmartphone,
  RefreshCw,
  SlidersHorizontal,
  type LucideIcon,
} from "lucide-react";
import type {
  NotificationClass,
  NotificationPreferenceChannel,
  NotificationPreferenceEvent,
} from "@playbookai/types";
import {
  AsyncSection,
  Button,
  SegmentedControl,
  Skeleton,
  cn,
} from "@playbookai/ui";
import { NOTIFICATION_CATEGORIES } from "./notification-presentation.js";

export type NotificationView = "all" | "unread";

interface InboxFiltersProps {
  readonly moduleOptions: readonly { value: string; label: string }[];
  readonly view: NotificationView;
  readonly category: NotificationClass | null;
  readonly moduleId: string | null;
  readonly onViewChange: (view: NotificationView) => void;
  readonly onCategoryChange: (category: NotificationClass | null) => void;
  readonly onModuleChange: (moduleId: string | null) => void;
}

export function InboxFilters({
  moduleOptions,
  view,
  category,
  moduleId,
  onViewChange,
  onCategoryChange,
  onModuleChange,
}: InboxFiltersProps): ReactElement {
  return (
    <nav
      aria-label="Notification filters"
      className="@min-[79rem]/notifications:sticky @min-[79rem]/notifications:top-4 @min-[79rem]/notifications:self-start"
    >
      <SegmentedControl<NotificationView>
        options={[
          { value: "all", label: "Inbox" },
          { value: "unread", label: "Unread" },
        ]}
        value={view}
        onChange={onViewChange}
        aria-label="Notification view"
        className="w-full [&>button]:flex-1 [&>button]:justify-center"
      />

      <FilterLabel className="mt-4">Category</FilterLabel>
      <ul className="grid grid-cols-2 gap-1 sm:grid-cols-3 @min-[79rem]/notifications:grid-cols-1">
        {NOTIFICATION_CATEGORIES.map((option) => {
          return (
            <li key={option.value}>
              <FilterButton
                icon={option.icon}
                label={option.label}
                active={category === option.value}
                onClick={() =>
                  onCategoryChange(
                    category === option.value ? null : option.value,
                  )
                }
              />
            </li>
          );
        })}
      </ul>

      <FilterLabel className="mt-4">Module</FilterLabel>
      <ul className="grid grid-cols-2 gap-1 sm:grid-cols-3 @min-[79rem]/notifications:grid-cols-1">
        {moduleOptions.map((option) => (
          <li key={option.value}>
            <FilterButton
              label={option.label}
              active={moduleId === option.value}
              onClick={() =>
                onModuleChange(moduleId === option.value ? null : option.value)
              }
            />
          </li>
        ))}
      </ul>
    </nav>
  );
}

function FilterLabel({
  children,
  className,
}: {
  readonly children: string;
  readonly className?: string;
}): ReactElement {
  return (
    <p
      className={cn(
        "mb-2 text-dense-1 font-semibold text-pb-text-secondary",
        className,
      )}
    >
      {children}
    </p>
  );
}

function FilterButton({
  icon: Icon,
  label,
  active,
  disabled = false,
  onClick,
}: {
  readonly icon?: LucideIcon;
  readonly label: string;
  readonly active: boolean;
  readonly disabled?: boolean;
  readonly onClick: () => void;
}): ReactElement {
  return (
    // eslint-disable-next-line ds/no-hand-built -- kept: Button has no 32px step and centres a medium-weight label, and ListItem has no row padding or corner; this start-aligned 32px filter row has no PR 3 look row.
    <button
      type="button"
      aria-pressed={active}
      disabled={disabled}
      className={cn(
        "flex h-8 w-full items-center gap-2 rounded-md px-2 text-start text-body-small transition-colors motion-reduce:transition-none focus-ring active:pb-overlay-paint-strong disabled:cursor-not-allowed disabled:opacity-45",
        active
          ? "bg-pb-card-elevated text-pb-text ring-1 ring-pb-border"
          : "text-pb-text-muted hover:pb-overlay-paint hover:text-pb-text",
      )}
      onClick={onClick}
    >
      {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </button>
  );
}

export interface DeliveryQuickControls {
  readonly events: readonly NotificationPreferenceEvent[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly refreshError: Error | null;
  readonly savingChannel: NotificationPreferenceChannel | null;
  readonly saveError: Error | null;
  readonly onRetry: () => void;
  readonly onDismissError: () => void;
  readonly onSetAll: (
    channel: NotificationPreferenceChannel,
    enabled: boolean,
  ) => void;
}

interface InboxSummaryProps {
  /** `null` when the total is unknown — never shown as zero. */
  readonly unreadCount: number | null;
  /**
   * Whether there is anything to mark read. Separate from `unreadCount`: with
   * the total unknown, a loaded page can still prove unread items exist.
   */
  readonly hasUnread: boolean;
  readonly isRefreshing: boolean;
  readonly isMarkingAllRead: boolean;
  readonly actionError: Error | null;
  readonly delivery: DeliveryQuickControls;
  readonly onRefresh: () => void;
  readonly onMarkAllRead: () => void;
  readonly onDismissActionError: () => void;
  readonly onOpenSettings: () => void;
}

export function InboxSummary({
  unreadCount,
  hasUnread,
  isRefreshing,
  isMarkingAllRead,
  actionError,
  delivery,
  onRefresh,
  onMarkAllRead,
  onDismissActionError,
  onOpenSettings,
}: InboxSummaryProps): ReactElement {
  return (
    <aside
      aria-label="Inbox summary"
      className="@min-[79rem]/notifications:sticky @min-[79rem]/notifications:top-4 @min-[79rem]/notifications:self-start"
    >
      {/* Both panels kept hand-built: Card paints its 1px inset top hairline
          (pb-hairline) on a flat default card, a look change no PR 3 row
          approves. */}
      <section className="rounded-xl border border-pb-border/80 bg-pb-card p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-body font-semibold text-pb-text">
              Inbox summary
            </h2>
            <p className="mt-0.5 text-dense-1 text-pb-text-muted">
              {unreadCount === null
                ? "Unread total unavailable"
                : unreadCount === 0
                  ? "You're all caught up"
                  : `${unreadCount} unread in total`}
            </p>
          </div>
          {/* Kept: Badge adds a 1px border and has no accent paint or 14px
              step; no PR 3 look row approves that. */}
          <span className="rounded-md bg-pb-accent-soft px-2 py-1 text-body-small font-semibold tabular-nums text-pb-accent-text">
            {unreadCount ?? "—"}
          </span>
        </div>

        {/* Kept hand-built, with its red Dismiss link: Alert paints the full
            soft fill, a 40% border and a glyph, and Button link has no
            critical tone; no PR 3 look row approves either. */}
        {actionError ? (
          <div
            role="alert"
            className="mt-3 rounded-md border border-pb-red/35 bg-pb-red-soft/45 p-2.5 text-dense-1 text-pb-red-text"
          >
            <p className="font-semibold">Notification action failed</p>
            <p className="mt-0.5">{actionError.message}</p>
            <Button
              variant="link"
              size="sm"
              className="mt-1 text-pb-red-text"
              onClick={onDismissActionError}
            >
              Dismiss
            </Button>
          </div>
        ) : null}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button
            variant="secondary"
            size="sm"
            loading={isRefreshing}
            onClick={onRefresh}
          >
            {!isRefreshing ? (
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            ) : null}
            {isRefreshing ? "Refreshing" : "Refresh"}
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={isMarkingAllRead}
            disabled={!hasUnread}
            onClick={onMarkAllRead}
          >
            {!isMarkingAllRead ? (
              <CheckCheck className="h-3.5 w-3.5" aria-hidden />
            ) : null}
            Mark all read
          </Button>
        </div>
      </section>

      <section
        id="notification-delivery"
        className="mt-3 rounded-xl border border-pb-border/80 bg-pb-card p-4"
      >
        <div className="flex items-center gap-2">
          <SlidersHorizontal
            className="h-4 w-4 text-pb-text-muted"
            aria-hidden
          />
          <h2 className="text-body-small font-semibold text-pb-text">
            Delivery
          </h2>
        </div>
        <p className="mt-1 text-dense-1 leading-relaxed text-pb-text-muted">
          Update every optional alert at once. Required alerts stay on.
        </p>

        <DeliveryControls controls={delivery} />

        <Button
          variant="link"
          size="sm"
          className="mt-3"
          onClick={onOpenSettings}
        >
          Advanced notification settings
        </Button>
      </section>
    </aside>
  );
}

function DeliveryControls({
  controls,
}: {
  readonly controls: DeliveryQuickControls;
}): ReactElement {
  if (controls.isLoading || controls.error) {
    /* The region's wait and its failed load through AsyncSection: the
       failure is the small error row with its Retry (OK-31). */
    return (
      <AsyncSection
        className="mt-3"
        status={controls.isLoading ? "loading" : "error"}
        size="sm"
        loadingLabel="delivery settings"
        errorTitle="Delivery settings couldn't load."
        onRetry={controls.onRetry}
        skeleton={
          <div className="flex flex-col gap-2">
            <Skeleton shape="panel" className="h-14" />
            <Skeleton shape="panel" className="h-14" />
          </div>
        }
      >
        {null}
      </AsyncSection>
    );
  }

  return (
    <div className="mt-3 flex flex-col gap-2">
      <DeliveryRow
        icon={MonitorSmartphone}
        label="In-app"
        channel="in_app"
        events={controls.events}
        savingChannel={controls.savingChannel}
        onSetAll={controls.onSetAll}
      />
      <DeliveryRow
        icon={Mail}
        label="Email"
        channel="email"
        events={controls.events}
        savingChannel={controls.savingChannel}
        onSetAll={controls.onSetAll}
      />
      {/* Kept hand-built, with their toned links: a notice about loaded
          settings, and Alert paints the full soft fill, a 40% border and a
          glyph; Button link has no critical or watch tone. */}
      {controls.saveError ? (
        <div
          role="alert"
          className="rounded-md border border-pb-red/35 bg-pb-red-soft/45 p-2.5 text-dense-1 text-pb-red-text"
        >
          <p>{controls.saveError.message}</p>
          <Button
            variant="link"
            size="sm"
            className="mt-1 text-pb-red-text"
            onClick={controls.onDismissError}
          >
            Dismiss
          </Button>
        </div>
      ) : null}
      {controls.refreshError ? (
        <div
          role="alert"
          className="rounded-md border border-pb-amber/35 bg-pb-amber-soft/45 p-2.5 text-dense-1 text-pb-amber-text"
        >
          <p>Latest delivery settings couldn't refresh.</p>
          <Button
            variant="link"
            size="sm"
            className="mt-1 text-pb-amber-text"
            onClick={controls.onRetry}
          >
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function DeliveryRow({
  icon: Icon,
  label,
  channel,
  events,
  savingChannel,
  onSetAll,
}: {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly channel: NotificationPreferenceChannel;
  readonly events: readonly NotificationPreferenceEvent[];
  readonly savingChannel: NotificationPreferenceChannel | null;
  readonly onSetAll: DeliveryQuickControls["onSetAll"];
}): ReactElement {
  const decisions = events.flatMap((event) => {
    const decision = event.channels.find(
      (candidate) => candidate.channel === channel,
    );
    return decision?.control === "switchable" ? [decision] : [];
  });
  const enabledCount = decisions.filter((decision) => decision.enabled).length;
  const allEnabled = decisions.length > 0 && enabledCount === decisions.length;
  const unavailable = decisions.length === 0;
  const action = unavailable
    ? "Unavailable"
    : allEnabled
      ? "Turn off"
      : "Turn on";
  const isSavingThisChannel = savingChannel === channel;

  /* Kept hand-built: SettingRow variant="card" is an 8px, px-4 py-3 box with
     no fill, and IconTile adds a border and the elevated fill to the glyph
     tile; no PR 3 look row approves either. */
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-pb-border/70 bg-pb-card-elevated/35 p-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-pb-page/70 text-pb-text-secondary">
        <Icon className="h-3.5 w-3.5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-dense-1 font-semibold text-pb-text">{label}</p>
        <p className="text-dense-2 text-pb-text-muted">
          {decisions.length === 0
            ? "Managed by workspace"
            : `${enabledCount} of ${decisions.length} optional alerts on`}
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        loading={isSavingThisChannel}
        disabled={unavailable || savingChannel !== null}
        aria-label={
          unavailable
            ? `${label} notifications unavailable`
            : `${action} all optional ${label.toLocaleLowerCase()} notifications`
        }
        onClick={() => onSetAll(channel, !allEnabled)}
      >
        {action}
      </Button>
    </div>
  );
}
